// ═══════════════════════════════════════════════════════════════════════════════
// Helper home — overdue tasks summary card (centered text, background icon)
// ═══════════════════════════════════════════════════════════════════════════════

import type { KeyboardEvent } from 'react';
import { OverdueTasksClockWatermark } from './OverdueTasksClockWatermark';

type HelperOverdueTasksSummaryCardProps = {
  count: number | null;
  title: string;
  hint: string;
  placeholderLabel: string;
  ariaLabel: string;
  onClick: () => void;
};

export const HelperOverdueTasksSummaryCard = ({
  count,
  title,
  hint,
  placeholderLabel,
  ariaLabel,
  onClick,
}: HelperOverdueTasksSummaryCardProps) => {
  const hasOverdueTasks = (count ?? 0) >= 1;
  const countDisplay =
    count === null ? placeholderLabel : count > 99 ? '99+' : count;

  const cardClassName = hasOverdueTasks
    ? 'relative flex min-h-full min-w-0 cursor-pointer flex-col overflow-hidden rounded-3xl border border-rose-300 bg-gradient-to-br from-white via-rose-50/80 to-pink-100/70 p-4 text-center shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-[400px]:p-3 sm:p-4 dark:border-rose-300 dark:bg-gradient-to-br dark:from-gray-900 dark:via-rose-950/35 dark:to-gray-900'
    : 'relative flex min-h-full min-w-0 cursor-pointer flex-col overflow-hidden rounded-3xl border border-primary-200 bg-white p-4 text-center shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 min-[400px]:p-3 sm:p-4 dark:border-emerald-300 dark:bg-gray-900/70';

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <section
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={cardClassName}
    >
      <div
        className="pointer-events-none absolute -right-6 -top-5 opacity-[0.35] dark:opacity-60 min-[400px]:-right-5 min-[400px]:-top-4"
        aria-hidden
      >
        <OverdueTasksClockWatermark
          className="h-[148px] w-[148px] max-w-none min-[400px]:h-[128px] min-[400px]:w-[128px] sm:h-[148px] sm:w-[148px]"
          showAlert={hasOverdueTasks}
        />
      </div>

      <div className="relative z-10 flex min-h-0 flex-1 flex-col">
        <span className="shrink-0 text-sm font-semibold text-gray-900 min-[400px]:text-sm sm:text-sm dark:text-gray-100">
          {title}
        </span>
        <div className="flex flex-1 items-center justify-center py-2 min-[400px]:py-1 sm:py-2">
          <p className="text-[38px] font-bold leading-none text-gray-900 min-[400px]:text-2xl sm:text-[38px] dark:text-gray-100">
            {countDisplay}
          </p>
        </div>
        <p className="shrink-0 text-sm leading-normal text-gray-600 min-[400px]:max-sm:text-[0.83125rem] sm:text-sm dark:text-gray-200">
          {hint}
        </p>
      </div>
    </section>
  );
};
